/* FDP Video Block carousel
 *
 * Each carousel is initialized exclusively from its own
 * [data-video-block-section] wrapper. This prevents multiple FDP Video Block
 * sections, Tips & Tricks, and other Swipers from sharing state or handlers.
 */
(function (window, document) {
  'use strict';

  var existingApi = window.FDPVideoBlock;

  if (existingApi) {
    existingApi.initAll(document);
    return;
  }

  var api = {};
  window.FDPVideoBlock = api;

  function safePlay(video) {
    if (!video || typeof video.play !== 'function') return;

    var result = video.play();
    if (result && typeof result.catch === 'function') {
      result.catch(function () {
        // Autoplay may be rejected by a browser in edge cases. The video can
        // still be started by the next user interaction.
      });
    }
  }

  function getActiveSlide(swiper) {
    return swiper && swiper.slides ? swiper.slides[swiper.activeIndex] : null;
  }

  function getSlideIndex(slide) {
    var swiperIndex = slide.getAttribute('data-swiper-slide-index');
    if (swiperIndex !== null) {
      return parseInt(swiperIndex, 10) || 0;
    }

    var sectionIndex = parseInt(slide.getAttribute('data-video-slide-index'), 10);
    return isNaN(sectionIndex) ? 0 : Math.max(0, sectionIndex - 1);
  }

  function loadThumbnailVideo(slide) {
    if (!slide) return null;

    var background = slide.querySelector('.video-block__background-photo[data-video]');
    if (!background) return null;

    var videoLoad = background.querySelector('.video-load');
    var videoUrl = background.getAttribute('data-video');
    if (!videoLoad || !videoUrl) return null;

    var video = videoLoad.querySelector('video');
    if (!video) {
      video = document.createElement('video');
      video.src = videoUrl;
      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.playsInline = true;
      videoLoad.appendChild(video);
    }

    safePlay(video);
    return video;
  }

  function pauseThumbnailVideosExcept(carousel, activeSlide) {
    var videos = carousel.querySelectorAll('.video-load video');

    Array.prototype.forEach.call(videos, function (video) {
      if (!activeSlide || !activeSlide.contains(video)) {
        video.pause();
      }
    });
  }

  function removeModals(carousel) {
    var modals = carousel.querySelectorAll('.youtube-video-modal');
    Array.prototype.forEach.call(modals, function (modal) {
      modal.remove();
    });
  }

  function openModal(carousel, slide, youtubeId, videoFull) {
    if (!slide || (!youtubeId && !videoFull)) return;

    removeModals(carousel);

    var modal = document.createElement('div');
    modal.className = 'youtube-video-modal';

    var closeButton = document.createElement('button');
    closeButton.className = 'close-modal';
    closeButton.innerHTML = '<span></span>';
    closeButton.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      modal.remove();
    });
    modal.appendChild(closeButton);

    if (youtubeId) {
      var isVertical = slide.classList.contains('vertical-video');
      var iframe = document.createElement('iframe');

      iframe.src = 'https://www.youtube-nocookie.com/embed/' + youtubeId + '?autoplay=1';
      iframe.title = 'YouTube video player';
      iframe.frameBorder = '0';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.width = isVertical ? '720' : '1280';
      iframe.height = isVertical ? '1280' : '720';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      modal.appendChild(iframe);
    } else {
      var background = slide.querySelector('.video-block__background-photo');
      var video = document.createElement('video');
      var volume = background ? parseFloat(background.getAttribute('data-video-full-volume')) : NaN;

      video.src = videoFull;
      video.autoplay = true;
      video.playsInline = true;
      video.controls = true;
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'contain';
      video.volume = isNaN(volume) ? 0.4 : volume;
      modal.appendChild(video);
      safePlay(video);
    }

    slide.appendChild(modal);
  }

  function hydrateImages(carousel) {
    var images = carousel.querySelectorAll('img[datasrc]');

    Array.prototype.forEach.call(images, function (image) {
      image.setAttribute('src', image.getAttribute('datasrc'));
      image.removeAttribute('datasrc');
    });
  }

  function observeViewport(carousel, swiper, enableAutoplay) {
    function handleEntry() {
      if (swiper.destroyed) return;

      hydrateImages(carousel);
      swiper._viewportTriggered = true;

      if (enableAutoplay) {
        loadThumbnailVideo(getActiveSlide(swiper));
      }
    }

    if (!('IntersectionObserver' in window)) {
      handleEntry();
      return;
    }

    var observer = new window.IntersectionObserver(function (entries) {
      Array.prototype.forEach.call(entries, function (entry) {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          handleEntry();
        }
      });
    }, { rootMargin: '500px 0px' });

    observer.observe(carousel);
  }

  function initialiseCounter(section) {
    var counterUp = window.counterUp && (window.counterUp.default || window.counterUp);
    var counters = section.querySelectorAll('.counter');

    if (!counterUp || !counters.length) return;

    function startCounter(counter) {
      if (counter.getAttribute('data-counter-initialized') === 'true') return;

      counterUp(counter, { duration: 2000, delay: 16 });
      counter.setAttribute('data-counter-initialized', 'true');
    }

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(counters, startCounter);
      return;
    }

    var observer = new window.IntersectionObserver(function (entries) {
      Array.prototype.forEach.call(entries, function (entry) {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          startCounter(entry.target);
        }
      });
    }, { threshold: 0.1 });

    Array.prototype.forEach.call(counters, function (counter) {
      observer.observe(counter);
    });
  }

  function initialiseSection(section) {
    if (!section || section.getAttribute('data-fdp-video-block-initialized') === 'true') return;
    if (typeof window.Swiper === 'undefined') return;

    var carousel = section.querySelector('[data-video-block-carousel]');
    if (!carousel) return;

    section.setAttribute('data-fdp-video-block-initialized', 'true');

    var enableAutoplay = carousel.getAttribute('data-enable-autoplay') === 'true';
    var auditMode = carousel.getAttribute('data-audit-mode') === 'true';
    var swiper = new window.Swiper(carousel, {
      speed: 600,
      grabCursor: true,
      slidesPerView: 'auto',
      centeredSlides: true,
      loop: true,
      spaceBetween: 0,
      observer: true,
      observeParents: true,
      slideToClickedSlide: auditMode,
      on: {
        beforeTransitionStart: function () {
          if (enableAutoplay && this._viewportTriggered) {
            loadThumbnailVideo(getActiveSlide(this));
          }
        },
        transitionStart: function () {
          removeModals(carousel);
          pauseThumbnailVideosExcept(carousel, getActiveSlide(this));
        },
        transitionEnd: function () {
          if (enableAutoplay && this._viewportTriggered) {
            loadThumbnailVideo(getActiveSlide(this));
          }
        },
        slideChangeTransitionEnd: function () {
          var pendingYoutubeId = carousel.getAttribute('data-pending-youtube-id');
          var pendingVideoFull = carousel.getAttribute('data-pending-video-full');

          if (!pendingYoutubeId && !pendingVideoFull) return;

          carousel.removeAttribute('data-pending-youtube-id');
          carousel.removeAttribute('data-pending-video-full');
          openModal(carousel, getActiveSlide(this), pendingYoutubeId, pendingVideoFull);
        }
      }
    });

    swiper._viewportTriggered = false;
    section._fdpVideoBlockSwiper = swiper;

    carousel.addEventListener('click', function (event) {
      var target = event.target.closest('.video-block__background-photo[data-youtube-id], .video-block__background-photo[data-video-full], .video-block__content');
      if (!target || !carousel.contains(target)) return;
      if (event.target.closest('.youtube-video-modal, .close-modal')) return;

      // Keep clicks inside this instance so other Video Block or Tips & Tricks
      // sections cannot receive or act on this interaction.
      event.stopPropagation();

      var slide = target.closest('.swiper-slide');
      var background = target.classList.contains('video-block__background-photo')
        ? target
        : slide.querySelector('.video-block__background-photo');
      if (!background) return;

      var youtubeId = background.getAttribute('data-youtube-id') || '';
      var videoFull = background.getAttribute('data-video-full') || '';
      if (!youtubeId && !videoFull) return;

      if (slide.classList.contains('swiper-slide-active')) {
        openModal(carousel, slide, youtubeId, videoFull);
        return;
      }

      carousel.setAttribute('data-pending-youtube-id', youtubeId);
      carousel.setAttribute('data-pending-video-full', videoFull);
      swiper.slideToLoop(getSlideIndex(slide));
    });

    observeViewport(carousel, swiper, enableAutoplay);
    initialiseCounter(section);

    window.setTimeout(function () {
      if (!swiper.destroyed) swiper.update();
    }, 100);
  }

  api.initSection = initialiseSection;
  api.initAll = function (scope) {
    var root = scope || document;
    var sections = [];

    if (root.matches && root.matches('[data-video-block-section]')) {
      sections.push(root);
    }

    Array.prototype.push.apply(sections, root.querySelectorAll('[data-video-block-section]'));
    Array.prototype.forEach.call(sections, initialiseSection);
  };

  api.initAll(document);

  document.addEventListener('shopify:section:load', function (event) {
    api.initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target.matches && event.target.matches('[data-video-block-section]')
      ? event.target
      : event.target.querySelector('[data-video-block-section]');
    var swiper = section && section._fdpVideoBlockSwiper;

    if (swiper && !swiper.destroyed) {
      swiper.destroy(true, true);
    }
  });
})(window, document);